/**
 * Zustand Store: Salesforce → Git Push Progress Manager
 *
 * Mirrors ingestJobStore/securityScanStore (module-scope, outside React's
 * component tree) but polls push_operations/{id} instead of subscribing to
 * SSE. Owning the poll interval here — rather than in a component's
 * useEffect — is what lets the push survive route changes and tab switches:
 * BranchDetailPane just renders whatever this store currently holds.
 *
 * Entries are keyed by repoFullName+branchName so BranchDetailPane can look
 * up the right in-flight push after remounting on the same branch.
 */

import { create } from 'zustand';
import { registerOrgScopedReset } from '@/lib/org-scope';
import { devtools } from 'zustand/middleware';
import { getQueryClient } from '@/dataProvider/reactQuery/context';
import { useActivityStore } from '@/stores/activityStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { pushSalesforceToGit, fetchPushOperation } from '@/services/git/api';
import type { PushOperation } from '@/services/git/types';

const POLL_INTERVAL_MS = 2000;

export interface SalesforcePushEntry {
  pushOperationId: string;
  repoFullName: string;
  branchName: string;
  status: PushOperation['status'];
  filesTotal: number;
  filesPushed: number;
  commitSha: string | null;
  commitUrl: string | null;
  error: string | null;
}

function pushKey(repoFullName: string, branchName: string): string {
  return `${repoFullName}::${branchName}`;
}

function activityIdFor(key: string): string {
  return `salesforce_push_${key}`;
}

interface SalesforcePushStore {
  pushes: Map<string, SalesforcePushEntry>;
  _intervals: Map<string, ReturnType<typeof setInterval>>;

  startPush: (repoFullName: string, branchName: string, commitMessage?: string) => Promise<void>;
  clearPush: (repoFullName: string, branchName: string) => void;
  getPush: (repoFullName: string, branchName: string) => SalesforcePushEntry | undefined;

  _poll: (repoFullName: string, branchName: string) => Promise<void>;
  _stopPolling: (key: string) => void;
}

export const useSalesforcePushStore = create<SalesforcePushStore>()(
  devtools(
    (set, get) => ({
      pushes: new Map(),
      _intervals: new Map(),

      getPush: (repoFullName, branchName) => get().pushes.get(pushKey(repoFullName, branchName)),

      _stopPolling: (key) => {
        const interval = get()._intervals.get(key);
        if (!interval) return;
        clearInterval(interval);
        set((state) => {
          const _intervals = new Map(state._intervals);
          _intervals.delete(key);
          return { _intervals };
        });
      },

      startPush: async (repoFullName, branchName, commitMessage) => {
        const orgId = useAuthStoreV1.getState().selectedOrg?.id;
        if (!orgId) return;

        const key = pushKey(repoFullName, branchName);
        const activityId = activityIdFor(key);

        get()._stopPolling(key);

        set((state) => {
          const pushes = new Map(state.pushes);
          pushes.set(key, {
            pushOperationId: '',
            repoFullName,
            branchName,
            status: 'pending',
            filesTotal: 0,
            filesPushed: 0,
            commitSha: null,
            commitUrl: null,
            error: null,
          });
          return { pushes };
        });

        useActivityStore.getState().startActivity(activityId, 'salesforce_push', 'Initiating push…', {
          repoFullName,
          branchName,
        });

        try {
          const result = await pushSalesforceToGit(orgId, repoFullName, commitMessage, branchName);

          set((state) => {
            const pushes = new Map(state.pushes);
            const entry = pushes.get(key);
            if (entry) pushes.set(key, { ...entry, pushOperationId: result.push_operation_id });
            return { pushes };
          });

          const interval = setInterval(() => { void get()._poll(repoFullName, branchName); }, POLL_INTERVAL_MS);
          set((state) => {
            const _intervals = new Map(state._intervals);
            _intervals.set(key, interval);
            return { _intervals };
          });
          void get()._poll(repoFullName, branchName);
        } catch (err) {
          const message = (err as Error)?.message ?? 'Failed to start push';
          set((state) => {
            const pushes = new Map(state.pushes);
            const entry = pushes.get(key);
            if (entry) pushes.set(key, { ...entry, status: 'failed', error: message });
            return { pushes };
          });
          useActivityStore.getState().failActivity(activityId, message);
          useActivityStore.getState().removeActivity(activityId);
        }
      },

      _poll: async (repoFullName, branchName) => {
        const orgId = useAuthStoreV1.getState().selectedOrg?.id;
        const key = pushKey(repoFullName, branchName);
        const entry = get().pushes.get(key);
        if (!orgId || !entry?.pushOperationId) return;

        const activityId = activityIdFor(key);

        try {
          const op = await fetchPushOperation(orgId, entry.pushOperationId);

          set((state) => {
            const pushes = new Map(state.pushes);
            const current = pushes.get(key);
            if (!current) return state;
            pushes.set(key, {
              ...current,
              status: op.status,
              filesTotal: op.files_total,
              filesPushed: op.files_pushed,
              commitSha: op.commit_sha,
              commitUrl: op.commit_url,
              error: op.error,
            });
            return { pushes };
          });

          if (op.status === 'pending' || op.status === 'in_progress') {
            const progress = op.files_total > 0 ? Math.round((op.files_pushed / op.files_total) * 100) : 0;
            useActivityStore.getState().updateActivity(activityId, {
              progress,
              message: op.status === 'pending'
                ? 'Preparing push…'
                : `Pushing files… (${op.files_pushed}/${op.files_total})`,
            });
            return;
          }

          get()._stopPolling(key);

          if (op.status === 'completed') {
            useActivityStore.getState().completeActivity(
              activityId,
              `Pushed ${op.files_pushed} file${op.files_pushed !== 1 ? 's' : ''}`
            );
            useActivityStore.getState().removeActivity(activityId);
            getQueryClient().invalidateQueries({ queryKey: ['gitBranches', orgId, repoFullName] });
          } else if (op.status === 'failed') {
            useActivityStore.getState().failActivity(activityId, op.error ?? 'Push failed');
            useActivityStore.getState().removeActivity(activityId);
          }
        } catch (err) {
          get()._stopPolling(key);
          const message = (err as Error)?.message ?? 'Failed to fetch push status';
          set((state) => {
            const pushes = new Map(state.pushes);
            const current = pushes.get(key);
            if (current) pushes.set(key, { ...current, status: 'failed', error: message });
            return { pushes };
          });
          useActivityStore.getState().failActivity(activityId, message);
          useActivityStore.getState().removeActivity(activityId);
        }
      },

      clearPush: (repoFullName, branchName) => {
        const key = pushKey(repoFullName, branchName);
        get()._stopPolling(key);
        set((state) => {
          const pushes = new Map(state.pushes);
          pushes.delete(key);
          return { pushes };
        });
      },
    }),
    { name: 'salesforce-push-store' }
  )
);

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Pushes are keyed by repo + branch, and the repo connection belongs to one
// organization — `startPush` reads `selectedOrg.id` to decide what to push. A
// push left in the map after a switch shows Org A's progress on a branch Org B
// may not even have connected.
//
// This one is discarded rather than partitioned, because the polling interval it
// owns would otherwise keep hitting the API for an organization nobody is
// looking at. The push itself completes server-side; the branch view re-reads it
// from `pushOperation` on return.
registerOrgScopedReset('salesforcePushStore', () => {
  const { pushes, _stopPolling } = useSalesforcePushStore.getState();
  for (const key of pushes.keys()) _stopPolling(key);
  useSalesforcePushStore.setState({ pushes: new Map(), _intervals: new Map() });
});
